import React, { useEffect, useState } from 'react';
import { X, Check, AlertCircle, Loader2, GraduationCap } from 'lucide-react';
import styles from './Plans.module.css';
import { authenticatedFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import SubscriptionViewer from '../SubscriptionViewer/SubscriptionViewer';
import { useDocumentHead } from '../../hooks/useDocumentHead';
import MinutesPurchase from '../MinutesPurchase/MinutesPurchase';

const Plans = ({ onClose, currentPlan, isLoading }) => {
    useDocumentHead(
        'Subscription Plans & Pricing | TopSpeaks',
        'Choose from our flexible subscription plans - Basic, Student, and Professional. Access AI-powered speaking tools with flexible recording times.',
        'index, follow'
    );
    const [subscriptionTypes, setSubscriptionTypes] = useState({
        free: 'monthly',
        student: 'yearly',
        pro: 'yearly'
    });
    const [switchLoadingStates, setSwitchLoadingStates] = useState({
        free: false,
        student: false,
        pro: false
    });
    const [timeRemaining, setTimeRemaining] = useState({});
    const [error, setError] = useState(null);
    const { updateUserPlan } = useAuth();
    const [activeView, setActiveView] = useState('plans');

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        if (!currentPlan?.limits) return;

        const calculateTimeRemaining = () => {
            const newTimeRemaining = {};
            const now = new Date();

            // Calculate for minutes and voice coaching (24-hour window)
            ['non_live_minutes'].forEach(type => {
                const lastReset = new Date(currentPlan.limits[type].last_reset);
                const nextReset = new Date(lastReset.getTime() + (currentPlan.limits[type].window_hours * 60 * 60 * 1000));
                const diff = nextReset - now;

                if (diff > 0) {
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    newTimeRemaining[type] = `${hours}h ${minutes}m`;
                } else {
                    newTimeRemaining[type] = 'Resetting...';
                }
            });

            // Calculate for AI presentations (30-day window)
            const aiLastReset = new Date(currentPlan.limits.ai_presentation.last_reset);
            const aiNextReset = new Date(aiLastReset.getTime() + (currentPlan.limits.ai_presentation.window_days * 24 * 60 * 60 * 1000));
            const aiDiff = aiNextReset - now;

            if (aiDiff > 0) {
                const days = Math.floor(aiDiff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((aiDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                newTimeRemaining.ai_presentation = `${days}d ${hours}h`;
            } else {
                newTimeRemaining.ai_presentation = 'Resetting...';
            }

            setTimeRemaining(newTimeRemaining);
        };

        calculateTimeRemaining();
        const timer = setInterval(calculateTimeRemaining, 60000); // Update every minute

        return () => clearInterval(timer);
    }, [currentPlan]);

    const handleToggleView = () => {
        setActiveView(prevView =>
            prevView === 'plans' ? 'minutes' : 'plans'
        );
    };

    const handleSubscriptionTypeChange = (planType, newType) => {
        setSubscriptionTypes(prev => ({
            ...prev,
            [planType]: newType
        }));
    };

    const handleSwitchPlan = async (targetPlan, subType) => {
        setError(null);
        setSwitchLoadingStates(prev => ({
            ...prev,
            [targetPlan]: true
        }));

        try {
            const response = await authenticatedFetch('/api/plans/switch', {
                method: 'POST',
                body: {
                    plan: targetPlan,
                    subscription_type: targetPlan === 'free' ? null : subType
                }
            });

            if (response.status === 'error' || !response.plan) {
                throw new Error(response.error || 'Failed to switch plan');
            }

            await updateUserPlan();
            alert(`Successfully switched to ${targetPlan === 'free' ? 'Basic' : targetPlan === 'student' ? 'Student' : 'Professional'} Plan`);
            onClose();
        } catch (error) {
            setError(error.message || 'Failed to switch plan. Please try again.');
        } finally {
            setSwitchLoadingStates(prev => ({
                ...prev,
                [targetPlan]: false
            }));
        }
    };

    const formatMinutes = (minutes) => {
        if (minutes < 1) {
            return '0 ';
        }
        return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    };

    const renderUsageBar = (used, limit) => {
        const percentage = Math.min((used / limit) * 100, 100);
        return (
            <div className={styles.usageBarContainer}>
                <div
                    className={styles.usageBar}
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: percentage > 90 ? '#dc2626' : percentage > 75 ? '#ea580c' : '#2563eb'
                    }}
                />
            </div>
        );
    };

    const renderUsageData = () => (
        <div className={styles.planFeatures}>
            <h4>Usage Analytics</h4>
            <div className={styles.usageList}>
                <div className={styles.usageItem}>
                    <div className={styles.usageLabel}>
                        <span>Recording Time</span>
                        <span className={styles.usageCount}>
                            {currentPlan.limits.non_live_minutes.used} / {formatMinutes(currentPlan.limits.non_live_minutes.limit)}
                        </span>
                    </div>
                    {renderUsageBar(
                        currentPlan.limits.non_live_minutes.used,
                        currentPlan.limits.non_live_minutes.limit
                    )}
                    <span className={styles.usagePeriod}>
                        Resets in: {timeRemaining.non_live_minutes || 'Calculating...'}
                    </span>
                </div>
            </div>
        </div>
    );

    const renderFeaturesList = (planType) => {
        const features = {
            free: {
                nonLiveTime: "1",
                voiceMsg: "5",
                aiGen: "10",
            },
            student: {
                nonLiveTime: "30",
                voiceMsg: "100",
                aiGen: "300",
            },
            pro: {
                nonLiveTime: "30",
                voiceMsg: "300",
                aiGen: "700",
            }
        };

        const plan = features[planType];

        return (
            <div className={styles.planFeatures}>
                <h4>Features</h4>
                <ul className={styles.featuresList}>
                    <li>
                        <Check size={16} />
                        <span>Recording Time {plan.nonLiveTime} min / 24 hours</span>
                    </li>
                    {planType === 'pro' && (
                        <li>
                            <Check size={16} />
                            <span>Advanced Analytics</span>
                        </li>
                    )}
                </ul>
            </div>
        );
    };

    const renderPricing = (planType) => {
        if (planType === 'free') {
            return (
                <div className={styles.priceContainer}>
                    <span className={styles.currency}>$</span>
                    <span className={styles.price}>0</span>
                    <span className={styles.period}>/month</span>
                </div>
            );
        }

        const prices = {
            student: {
                monthly: 3,
                yearly_total: 33 // 11 months × $3
            },
            pro: {
                monthly: 10,
                yearly_total: 110 // 11 months × $10
            }
        };

        const isCurrentPlan = currentPlan.plan === planType;
        if (isCurrentPlan && currentPlan.subscription?.type) {
            const price = currentPlan.subscription.type === 'yearly' ?
                prices[planType].yearly_total : prices[planType].monthly;
            const period = currentPlan.subscription.type === 'yearly' ? '/year' : '/month';

            return (
                <div className={styles.currentPlanPrice}>
                    <div className={styles.priceContainer}>
                        <span className={styles.currency}>$</span>
                        <span className={styles.price}>{price}</span>
                        <span className={styles.period}>{period}</span>
                    </div>
                    {currentPlan.subscription.type === 'yearly' && (
                        <span className={styles.savingsBadge}>1 month free</span>
                    )}
                </div>
            );
        }

        return (
            <div className={styles.pricingOptions}>
                <div
                    className={`${styles.priceOption} ${subscriptionTypes[planType] === 'yearly' ? styles.selectedPrice : ''}`}
                    onClick={() => handleSubscriptionTypeChange(planType, 'yearly')}
                >
                    <div className={styles.priceContainer}>
                        <span className={styles.currency}>$</span>
                        <span className={styles.price}>{prices[planType].yearly_total}</span>
                        <span className={styles.period}>/year</span>
                    </div>
                    <span className={styles.savingsBadge}>1 month free</span>
                </div>
                <div
                    className={`${styles.priceOption} ${subscriptionTypes[planType] === 'monthly' ? styles.selectedPrice : ''}`}
                    onClick={() => handleSubscriptionTypeChange(planType, 'monthly')}
                >
                    <div className={styles.priceContainer}>
                        <span className={styles.currency}>$</span>
                        <span className={styles.price}>{prices[planType].monthly}</span>
                        <span className={styles.period}>/month</span>
                    </div>
                </div>
            </div>
        );
    };

    const renderPlanCard = (planType) => {
        const isCurrentPlan = currentPlan.plan === planType;
        const planTitles = {
            free: 'Basic',
            student: (
                <div className={styles.planTitleWithIcon}>
                    <GraduationCap size={20} />
                    <span>Student</span>
                </div>
            ),
            pro: 'Professional'
        };

        return (
            <div className={`${styles.plan} ${isCurrentPlan ? styles.activePlan : ''}`}>
                {isCurrentPlan && (
                    <div className={styles.planBadge}>
                        Current Plan
                        <SubscriptionViewer subscription={currentPlan.subscription} />
                    </div>
                )}
                <div className={styles.planHeader}>
                    <h3>{planTitles[planType]}</h3>
                    {renderPricing(planType)}
                </div>

                {isCurrentPlan ? renderUsageData() : renderFeaturesList(planType)}

                {!isCurrentPlan && (
                    <button
                        className={`${styles.switchButton} ${switchLoadingStates[planType] ? styles.loading : ''}`}
                        onClick={() => handleSwitchPlan(planType, subscriptionTypes[planType])}
                        disabled={switchLoadingStates[planType]}
                    >
                        {switchLoadingStates[planType] ? (
                            <>
                                <Loader2 className={styles.spinnerIcon} size={16} />
                                Processing...
                            </>
                        ) : (
                            `Upgrade to ${planType === 'free'
                                ? 'Basic'
                                : planType === 'student'
                                    ? 'Student'
                                    : 'Professional'
                            }`
                        )}
                    </button>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className={styles.overlay}>
                <div className={styles.modal}>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                        <X size={24} />
                    </button>
                    <div className={styles.loadingContainer}>
                        <Loader2 className={styles.spinnerIcon} size={40} />
                        <p>Loading plan information...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!currentPlan) {
        return (
            <div className={styles.overlay}>
                <div className={styles.modal}>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                        <X size={24} />
                    </button>
                    <div className={styles.errorContainer}>
                        <AlertCircle size={40} />
                        <h3>Unable to Load Plans</h3>
                        <p>Please refresh the page or try again later.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <button className={styles.closeButton} onClick={onClose}>
                    <X size={24} />
                </button>

                <div className={styles.header}>
                    <button
                        className={styles.toggleViewButton}
                        onClick={handleToggleView}
                    >
                        {activeView === 'plans'
                            ? 'Purchase Minutes'
                            : 'View Plans'}
                    </button>
                    <h2>{activeView === 'plans' ? 'Plans & Pricing' : 'Purchase Minutes'}</h2>
                    <p>
                        {activeView === 'plans'
                            ? 'Choose the plan that best fits your presentation needs'
                            : 'Add more recording time to your current plan'}
                    </p>
                </div>

                {error && (
                    <div className={styles.errorMessage}>
                        <AlertCircle size={16} />
                        <span>{error}</span>
                    </div>
                )}
                {activeView === 'minutes' ? (
                    <MinutesPurchase
                        onClose={onClose}
                        currentPlan={currentPlan}
                    />
                ) : (
                    <div className={styles.plansContainer}>
                        {renderPlanCard('free')}
                        {renderPlanCard('student')}
                        {renderPlanCard('pro')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Plans;